import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { X, Save, ArrowLeft, Plus, AlertCircle } from 'lucide-react';
import RichTextEditor from './ui/rich-text-editor';
import ImageUpload from './ui/image-upload';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('FullScreenEditor Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
          <div className="text-center p-8">
            <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">There was an error loading the editor</p>
            <Button onClick={() => window.location.reload()} className="btn-base btn-primary">
              Reload Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const FullScreenEditor = ({ 
  type, // 'blog' or 'project'
  itemId, 
  onClose, 
  onSave 
}) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
  const authToken = localStorage.getItem('admin_token');

  useEffect(() => {
    if (itemId && itemId !== 'new') {
      loadItemData();
    } else {
      setInitialFormData();
    }
  }, [itemId, type]);

  const loadItemData = async () => {
    try {
      const endpoint = type === 'blog' ? `/content/blogs` : `/content/projects`;
      const response = await fetch(`${API_BASE}${endpoint}`);
      const items = await response.json();
      const item = items.find(i => i.id === itemId);
      
      if (item) {
        setFormData(item);
      } else {
        setInitialFormData();
      }
    } catch (error) {
      console.error('Error loading item:', error);
      setInitialFormData();
    } finally {
      setInitialLoad(false);
    }
  };

  const setInitialFormData = () => {
    if (type === 'blog') {
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        category: '',
        image: '',
        tags: [],
        published: false,
        updated_at: new Date().toISOString()
      });
    } else {
      setFormData({
        title: '',
        description: '',
        details: '',
        category: '',
        image: '',
        technologies: [],
        status: 'In Progress',
        impact: '',
        github_url: '',
        demo_url: '',
        updated_at: new Date().toISOString()
      });
    }
    setInitialLoad(false);
  };

  const handleSave = async () => {
    if (!formData.title || (type === 'blog' && !formData.content) || (type === 'project' && !formData.description)) {
      alert('Please fill in required fields');
      return;
    }

    // Update the timestamp when saving
    const dataToSave = {
      ...formData,
      updated_at: new Date().toISOString()
    };

    setLoading(true);
    try {
      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setLoading(false);
    }
  };

  const addTechnology = (tech) => {
    if (tech && !formData.technologies?.includes(tech)) {
      setFormData({
        ...formData,
        technologies: [...(formData.technologies || []), tech]
      });
    }
  };

  const removeTechnology = (tech) => {
    setFormData({
      ...formData,
      technologies: formData.technologies?.filter(t => t !== tech) || []
    });
  };

  const addTag = (tag) => {
    if (tag && !formData.tags?.includes(tag)) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tag]
      });
    }
  };

  const removeTag = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter(t => t !== tag) || []
    });
  };

  if (initialLoad) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button onClick={onClose} variant="ghost" size="sm">
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
            {itemId === 'new' ? `Create New ${type === 'blog' ? 'Blog Post' : 'Project'}` : `Edit ${type === 'blog' ? 'Blog Post' : 'Project'}`}
          </h1>
        </div>
        <div className="flex items-center space-x-3">
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            <Save size={16} className="mr-2" />
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Card className="p-8">
          {type === 'blog' ? (
            <div className="space-y-8">
              {/* Blog Title and Category */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Blog Title *</label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                    placeholder="Enter blog title..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select
                    value={formData.category || ''}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category...</option>
                    <option value="AI & Technology">AI & Technology</option>
                    <option value="Leadership">Leadership</option>
                    <option value="Investment Management">Investment Management</option>
                    <option value="Process Improvement">Process Improvement</option>
                    <option value="Team Building">Team Building</option>
                  </select>
                </div>
              </div>

              {/* Blog Image */}
              <div>
                <label className="block text-sm font-medium mb-2">Featured Image</label>
                
                {/* Image Upload Component */}
                <div className="mb-4">
                  <ImageUpload
                    currentImage={formData.image}
                    onImageChange={(imageUrl) => setFormData({...formData, image: imageUrl})}
                    onImageRemove={() => setFormData({...formData, image: ''})}
                    label="Upload Blog Image"
                    description="Click to upload or drag and drop (JPEG, PNG, GIF, WebP - Max 5MB)"
                    maxSize={5}
                  />
                </div>

                {/* OR URL Input */}
                <div className="text-center text-sm text-gray-500 mb-2">OR</div>
                <div>
                  <label className="block text-sm font-medium mb-2">Enter Image URL</label>
                  <input
                    type="url"
                    value={formData.image || ''}
                    onChange={(e) => setFormData({...formData, image: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>

              {/* Blog Excerpt */}
              <div>
                <label className="block text-sm font-medium mb-2">Excerpt</label>
                <RichTextEditor
                  value={formData.excerpt || ''}
                  onChange={(value) => setFormData({...formData, excerpt: value})}
                  placeholder="Brief description of the blog post..."
                  height="120px"
                />
              </div>

              {/* Blog Content */}
              <div>
                <label className="block text-sm font-medium mb-2">Content *</label>
                <RichTextEditor
                  value={formData.content || ''}
                  onChange={(value) => setFormData({...formData, content: value})}
                  placeholder="Write your blog content here..."
                  height="400px"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium mb-2">Tags</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(formData.tags || []).map((tag, index) => (
                    <Badge key={index} className="bg-purple-100 text-purple-700 px-3 py-1 text-sm flex items-center gap-2">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="text-purple-500 hover:text-purple-700">
                        <X size={14} />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="tag-input-fullscreen"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add tag..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addTag(e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Button 
                    onClick={() => {
                      const input = document.getElementById('tag-input-fullscreen');
                      addTag(input.value);
                      input.value = '';
                    }}
                    variant="outline"
                  >
                    <Plus size={16} />
                  </Button>
                </div>
              </div>

              {/* Published */}
              <div className="flex items-center space-x-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.published || false}
                    onChange={(e) => setFormData({...formData, published: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium">Publish immediately</span>
                </label>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Project Title and Category */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Project Title *</label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                    placeholder="Enter project title..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select
                    value={formData.category || ''}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category...</option>
                    <option value="Data Analytics & AI">Data Analytics & AI</option>
                    <option value="Process Automation">Process Automation</option>
                    <option value="AI & Productivity">AI & Productivity</option>
                    <option value="Data Engineering">Data Engineering</option>
                  </select>
                </div>
              </div>

              {/* Status */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={formData.status || 'In Progress'}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="In Progress">In Progress</option>
                    <option value="In Development">In Development</option>
                    <option value="Completed">Completed</option>
                    <option value="Planning">Planning</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Project Image</label>
                  
                  {/* Image Upload Component */}
                  <div className="mb-4">
                    <ImageUpload
                      currentImage={formData.image}
                      onImageChange={(imageUrl) => setFormData({...formData, image: imageUrl})}
                      onImageRemove={() => setFormData({...formData, image: ''})}
                      label="Upload Project Image"
                      description="Click to upload or drag and drop (JPEG, PNG, GIF, WebP - Max 5MB)"
                      maxSize={5}
                    />
                  </div>

                  {/* OR URL Input */}
                  <div className="text-center text-sm text-gray-500 mb-2">OR</div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Enter Image URL</label>
                    <input
                      type="url"
                      value={formData.image || ''}
                      onChange={(e) => setFormData({...formData, image: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                </div>
              </div>

              {/* Project Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Description *</label>
                <RichTextEditor
                  value={formData.description || ''}
                  onChange={(value) => setFormData({...formData, description: value})}
                  placeholder="Enter project description..."
                  height="200px"
                />
              </div>

              {/* Project Details */}
              <div>
                <label className="block text-sm font-medium mb-2">Project Details</label>
                <RichTextEditor
                  value={formData.details || ''}
                  onChange={(value) => setFormData({...formData, details: value})}
                  placeholder="Enter detailed project information, key achievements, technical highlights, etc..."
                  height="300px"
                />
              </div>

              {/* Project Impact */}
              <div>
                <label className="block text-sm font-medium mb-2">Impact</label>
                <RichTextEditor
                  value={formData.impact || ''}
                  onChange={(value) => setFormData({...formData, impact: value})}
                  placeholder="Describe the project impact..."
                  height="150px"
                />
              </div>

              {/* URLs */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">GitHub URL</label>
                  <input
                    type="url"
                    value={formData.github_url || ''}
                    onChange={(e) => setFormData({...formData, github_url: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter GitHub URL..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Demo URL</label>
                  <input
                    type="url"
                    value={formData.demo_url || ''}
                    onChange={(e) => setFormData({...formData, demo_url: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter demo URL..."
                  />
                </div>
              </div>

              {/* Technologies */}
              <div>
                <label className="block text-sm font-medium mb-2">Technologies</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(formData.technologies || []).map((tech, index) => (
                    <Badge key={index} className="bg-blue-100 text-blue-700 px-3 py-1 text-sm flex items-center gap-2">
                      {tech}
                      <button onClick={() => removeTechnology(tech)} className="text-blue-500 hover:text-blue-700">
                        <X size={14} />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="tech-input-fullscreen"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add technology..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addTechnology(e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Button 
                    onClick={() => {
                      const input = document.getElementById('tech-input-fullscreen');
                      addTechnology(input.value);
                      input.value = '';
                    }}
                    variant="outline"
                  >
                    <Plus size={16} />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
    </ErrorBoundary>
  );
};

export default FullScreenEditor;